<script lang="ts">
    // Svelte/Jet/Models/Types
    import { onDestroy, onMount } from 'svelte';
    //import type { LibrarySong } from '~/jet/intents/library/songs/types';
    // TODO
    interface LibrarySong {
        [k: string]: any;
    }
    import type {
        // Artwork as ArtworkType,
        // ContentDescriptor,
        TrackLockupLayoutKind,
    } from '~/jet/models';
    import { getJetPerform } from '~/jet';
    // import { addContainerItemsToPlayAction } from '~/jet/web-actions';

    // Stores
    import { getItemSelector } from '~/stores/item-selector';
    import { getI18n } from '~/stores/i18n';

    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import InteractivePlayButton from '~/components/jet/buttons/InteractivePlayButton.svelte';
    // import FavoriteButton from '~/components/jet/buttons/FavoriteButton.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';

    // Shared Components
    import TrackListItemLockup from '@amp/web-app-components/src/components/TrackList/TrackListItemLockup.svelte';

    // Utils
    import { formatDuration } from '~/utils/datetime';
    import { getLocale } from '~/utils/locale';
    import { getPlayback } from '~/utils/playback';
    // import {
    //     getContentId,
    //     getContentIdForLibraryState,
    // } from '~/utils/get-content-id';
    // import { getLocalLibraryState } from '~/utils/local-library-state';
    // import { toggleFavorite } from '~/utils/favorites';

    const i18n = getI18n();
    const locale = getLocale();
    const perform = getJetPerform();
    const { queue, isRestricted: getIsRestricted } = getPlayback();
    const itemSelector = getItemSelector();
    // const localLibraryState = getLocalLibraryState();

    export let item: LibrarySong | null;
    export let items: (LibrarySong | Promise<LibrarySong>)[] = [];
    export let rowIndex: number;
    export let isFirst: boolean = false;
    export let isLast: boolean = false;

    let isRestricted: boolean = false;

    // The `items` array passed into the component can contain Promises for items
    // that are still loading. This array is used to hold the resolved promises so
    // that other values can be safely derived from it.
    let resolvedItems: LibrarySong[] = [];

    onMount(async () => {
        try {
            const restricted = await getIsRestricted?.();
            isRestricted = restricted;
        } catch {
            // noop
        }
    });

    $: layoutKind = ((): TrackLockupLayoutKind => {
        const layoutStyle = item.layoutStyle;
        return typeof layoutStyle === 'string'
            ? layoutStyle
            : layoutStyle?.kind;
    })();

    $: isSelected =
        item.id &&
        $itemSelector.selectedItems.some((song) => song.id === item.id);
    $: artwork = item?.artwork || null;
    $: name = item?.title || '';
    $: workName = item?.workName || '';
    $: subtitle = item?.subtitle || '';
    $: durationInSeconds = Math.trunc(item?.duration / 1000) || null;
    $: formattedDuration =
        formatDuration(durationInSeconds, locale.language) || '';
    $: playAction = item?.playAction || null;
    $: isExplicit = item?.contentRating === 'explicit';
    // $: itemId = getContentId(item);
    $: itemId = item.id;
    // $: libraryStateId = getContentIdForLibraryState(item);
    // $: libraryStateId = item.id;
    // $: contentDescriptor = item?.contentDescriptor;
    // $: kind = contentDescriptor?.kind;

    // Not using the catalog ID here as it will affect playback states for library items
    $: isNowPlayingItem = itemId && itemId.includes($queue.nowPlayingItemId);
    $: isLoading = isNowPlayingItem && $queue.derivedStates.isLoading;
    $: isPlaying = isNowPlayingItem && $queue.derivedStates.isPlaying;
    $: isPaused = isNowPlayingItem && $queue.derivedStates.isPaused;
    $: isDisabled = item?.isDisabled || (isExplicit && isRestricted);
    // $: updatedState = localLibraryState.subscribeToUpdates(libraryStateId);
    // $: inFavorites =
    //     $updatedState &&
    //     localLibraryState.get(libraryStateId, kind)?.inFavorites;

    // Asynchronously update `resolvedItems` when `items` prop changes.
    $: Promise.all(items).then((results) => (resolvedItems = results));
    // $: containerItemDescriptors = resolvedItems.map((t) => t.contentDescriptor);

    $: itemWithUpdatedPlayAction = (() => {
        return {
            ...item,
            playAction: !isDisabled && item.playAction,
            // addContainerItemsToPlayAction(
            //     item.playAction,
            //     containerItemDescriptors,
            // ),
        };
    })();

    function play() {
        playAction && perform(itemWithUpdatedPlayAction.playAction);
    }

    function handleClick(e: MouseEvent) {
        itemSelector.handleClick(item as any, e);
    }

    // const handleToggleFavorite = (e: CustomEvent<boolean>): void => {
    //     toggleFavorite(
    //         libraryStateId,
    //         kind,
    //         contentDescriptor,
    //         !e.detail,
    //         localLibraryState,
    //         false,
    //         true,
    //         perform,
    //     );
    // };

    // onDestroy(() => {
    //     localLibraryState.unsubscribeFromUpdates(libraryStateId);
    // });
</script>

<TrackListItemLockup
    {item}
    {layoutKind}
    {rowIndex}
    {isFirst}
    {isLast}
    {isSelected}
    isLastSelected={$itemSelector.selectedItems[
        $itemSelector.selectedItems.length - 1
    ] === item}
    showSecondColumn={true}
    showThirdColumn={true}
    showFourthColumn={false}
    showArtwork={true}
    {isLoading}
    {isPlaying}
    {isPaused}
    {isDisabled}
    itemSelectorFn={itemSelector}
    on:keyDownPlayAction={play}
    on:trackListItemDoubleClick={play}
>
    <Artwork
        {artwork}
        forceFullWidth={false}
        profile="track-list"
        slot="artwork-with-badge"
    />

    <InteractivePlayButton
        item={itemWithUpdatedPlayAction}
        slot="interactive-play-button"
    />

    <svelte:fragment slot="song-click-action">
        {name}
    </svelte:fragment>

    <svelte:fragment slot="second-column-data">
        {workName}
    </svelte:fragment>

    <svelte:fragment slot="third-column-data">
        {subtitle}
    </svelte:fragment>

    <svelte:fragment slot="last-column-data">
        <div class="track-length">
            {formattedDuration}
        </div>

        <div class="track-context-menu">
            <ProductContextMenu
                contextMenuAction={item.contextMenuAction}
                hasMaterial={false}
                buttonStyle="non-platter"
            />
        </div>
    </svelte:fragment>
</TrackListItemLockup>

<style lang="scss">
    // NOTE: Library track style overrides are located in the <Tracks />
    // component. Applying a wrapper class to this component will break
    // the AX tree such that the entire tracklist will become inaccessible
    // to VO. See: rdar://144260535

    .track-length {
        grid-area: song-controls-length;
    }

    .track-context-menu {
        grid-area: song-controls-context;
    }
</style>
